import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { useFundingCalcPrompt, useWizardFundingSources } from './useFundingCalcPrompt';

const SOFT_TOKEN_WARN_THRESHOLD = 150_000;
const ALL_SOURCES_VALUE = '__all__';

export function WizardFundingCalcPromptPage() {
  const [clientIdInput, setClientIdInput] = useState('');
  const [lookedUpClientId, setLookedUpClientId] = useState<string | null>(null);
  const [selectedFundingSourceId, setSelectedFundingSourceId] = useState<string>(
    ALL_SOURCES_VALUE,
  );
  const [activeFundingSourceId, setActiveFundingSourceId] = useState<string | null>(null);
  const [generateRequested, setGenerateRequested] = useState(false);

  const sourcesQuery = useWizardFundingSources(lookedUpClientId);
  const promptQuery = useFundingCalcPrompt(
    lookedUpClientId,
    activeFundingSourceId,
    generateRequested,
  );

  // Reset selection whenever a new client is looked up.
  useEffect(() => {
    setSelectedFundingSourceId(ALL_SOURCES_VALUE);
    setActiveFundingSourceId(null);
    setGenerateRequested(false);
  }, [lookedUpClientId]);

  const onLookup = () => {
    const trimmed = clientIdInput.trim();
    if (!trimmed) {
      toast.error('Enter a client ID.');
      return;
    }
    if (trimmed === lookedUpClientId) {
      sourcesQuery.refetch();
    } else {
      setLookedUpClientId(trimmed);
    }
  };

  const onGenerate = () => {
    const nextFundingSourceId =
      selectedFundingSourceId === ALL_SOURCES_VALUE ? null : selectedFundingSourceId;
    if (
      generateRequested &&
      nextFundingSourceId === activeFundingSourceId &&
      lookedUpClientId
    ) {
      promptQuery.refetch();
    } else {
      setActiveFundingSourceId(nextFundingSourceId);
      setGenerateRequested(true);
    }
  };

  const onCopy = async () => {
    if (!promptQuery.data) return;
    try {
      await navigator.clipboard.writeText(promptQuery.data.prompt);
      toast.success('Prompt copied to clipboard');
    } catch (err) {
      toast.error('Clipboard write failed');
      console.error(err);
    }
  };

  const tokensAboveThreshold =
    promptQuery.data && promptQuery.data.tokenEstimate > SOFT_TOKEN_WARN_THRESHOLD;

  const sources = sourcesQuery.data?.fundingSources ?? [];

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="LTC Funding Calc Prompt"
        description="Assemble a prompt bundling funding calc source + execution trace + client inputs. Paste into your own LLM session."
      />

      <Card className="max-w-3xl border border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle>Generate prompt</CardTitle>
          <CardDescription className="text-gray-500">
            Look up a client to see their funding sources, then pick which one to trace.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="clientId">Client ID</Label>
            <div className="flex gap-2">
              <Input
                id="clientId"
                value={clientIdInput}
                onChange={e => setClientIdInput(e.target.value)}
                placeholder="e.g. 01abc123..."
                autoComplete="off"
              />
              <Button
                type="button"
                onClick={onLookup}
                disabled={sourcesQuery.isFetching || !clientIdInput.trim()}
              >
                {sourcesQuery.isFetching && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {sourcesQuery.isFetching ? 'Looking up…' : 'Look up'}
              </Button>
            </div>
          </div>

          {sourcesQuery.isError && (
            <p className="text-sm text-red-600">
              Lookup failed: {(sourcesQuery.error as Error)?.message ?? 'unknown error'}
            </p>
          )}

          {sourcesQuery.data && (
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Client:</span>{' '}
                {sourcesQuery.data.clientSummary}
              </p>

              <div className="space-y-2">
                <Label htmlFor="fundingSourceId">Funding source</Label>
                <Select
                  value={selectedFundingSourceId}
                  onValueChange={setSelectedFundingSourceId}
                >
                  <SelectTrigger
                    id="fundingSourceId"
                    className="h-auto min-h-12 w-full py-2 [&>span]:!line-clamp-none [&>span]:flex [&>span]:flex-col [&>span]:items-start [&>span]:text-left"
                  >
                    <SelectValue placeholder="Select a funding source" />
                  </SelectTrigger>
                  <SelectContent className="w-[var(--radix-select-trigger-width)] min-w-[36rem]">
                    <SelectItem value={ALL_SOURCES_VALUE}>
                      <div className="flex flex-col">
                        <span className="font-medium">All funding sources</span>
                        <span className="text-xs text-gray-500">
                          Trace every policy and self-funding source
                        </span>
                      </div>
                    </SelectItem>
                    {sources.map(s => (
                      <SelectItem key={s.fundingSourceId} value={s.fundingSourceId}>
                        <div className="flex w-full flex-col">
                          <span className="font-medium">
                            {s.primaryLabel}
                            <span className="ml-2 text-xs font-normal text-gray-400">
                              {s.kind === 'ltcPolicy' ? 'LTC Policy' : 'Self-funded'}
                            </span>
                          </span>
                          <span className="whitespace-normal break-all text-xs text-gray-500">
                            {s.secondaryLabel}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {sources.length === 0 && (
                  <p className="text-xs text-gray-500">
                    This client has no funding sources — only client-level derivatives will
                    be traced.
                  </p>
                )}
              </div>

              <Button
                type="button"
                onClick={onGenerate}
                disabled={promptQuery.isFetching}
              >
                {promptQuery.isFetching && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {promptQuery.isFetching ? 'Generating…' : 'Generate prompt'}
              </Button>
            </div>
          )}

          {promptQuery.isError && (
            <p className="text-sm text-red-600">
              Failed to generate prompt:{' '}
              {(promptQuery.error as Error)?.message ?? 'unknown error'}
            </p>
          )}

          {promptQuery.data && (
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Estimated tokens:</span>{' '}
                {promptQuery.data.tokenEstimate.toLocaleString()}
                {tokensAboveThreshold && (
                  <span className="ml-2 text-amber-600">
                    (above {SOFT_TOKEN_WARN_THRESHOLD.toLocaleString()} — may exceed some
                    LLM context windows)
                  </span>
                )}
              </p>

              <Button
                type="button"
                size="lg"
                onClick={onCopy}
                className="bg-primary hover:bg-primary-hover"
              >
                Copy prompt to clipboard
              </Button>

              <details className="pt-2">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                  Preview prompt
                </summary>
                <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-gray-50 p-3 text-xs">
                  {promptQuery.data.prompt}
                </pre>
              </details>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
